import React, { useState } from 'react'
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Box, useTheme, Typography, Divider, CircularProgress, Chip, Alert } from "@mui/material";
import { tokens } from "../../../theme";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckSharpIcon from '@mui/icons-material/CheckSharp';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import Axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import Dropdown from '../../../components/dropdown/Dropdown';
import { getAuthHeader } from '../../../utils/authToken';
import dayjs from 'dayjs';

const NA_VALUE = "N/A";
const preloadTestRef = (val) => (val == null ? NA_VALUE : String(val));

const UpdateModal = ({
    eventModule,
    special_notes,
    courseStudents,
    courseId,
    setAnchorEl,
    setBackdropOpen,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
    userRole,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const matches = useMediaQuery('(min-width:1500px)');
    const matches2 = useMediaQuery('(min-width:1200px)');
    const isNonMobile = useMediaQuery("(min-width:600px)");

    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {setOpen(false); setAnchorEl(null)};

    const prevChapter = React.useState(eventModule.progress);
    const prevFrom = React.useState(eventModule.progress_from);
    const prevTo = React.useState(eventModule.progress_to);
    const prevChapter2 = React.useState(eventModule.progress_2);
    const prevFrom2 = React.useState(eventModule.progress_from_2);
    const prevTo2 = React.useState(eventModule.progress_to_2);
    const prevChapter3 = React.useState(eventModule.progress_3);
    const prevFrom3 = React.useState(eventModule.progress_from_3);
    const prevTo3 = React.useState(eventModule.progress_to_3);

    // for adding progress chapter
    const [isProgressAdded, setIsProgressAdded] = React.useState(
        prevChapter3[0] !== null ? 3 : prevChapter2[0] !== null ? 2 : 1
    );
    const firstProgressCount = React.useState(prevChapter3[0] !== null ? 3 : prevChapter2[0] !== null ? 2 : 1);

    // test_ref dropdown state — preload from saved values (null → N/A)
    const [testOptions, setTestOptions] = React.useState([{ value: NA_VALUE, label: "N/A" }]);
    const [testRef1, setTestRef1] = React.useState(preloadTestRef(eventModule.test_ref_1));
    const [testRef2, setTestRef2] = React.useState(preloadTestRef(eventModule.test_ref_2));
    const [testRef3, setTestRef3] = React.useState(preloadTestRef(eventModule.test_ref_3));
    const [staleTestRefWarning, setStaleTestRefWarning] = React.useState(null);
    const prevTestRef1 = React.useState(preloadTestRef(eventModule.test_ref_1));
    const prevTestRef2 = React.useState(preloadTestRef(eventModule.test_ref_2));
    const prevTestRef3 = React.useState(preloadTestRef(eventModule.test_ref_3));

    // Fetch test options for this course; auto-revert any preloaded test_ref
    // that's no longer in the course's current v2_course_tests.
    React.useEffect(() => {
        if (!courseId) return;
        Axios.get(`${process.env.REACT_APP_URL}/api/v2/courses/${courseId}/tests`, {
            headers: { Authorization: getAuthHeader() },
        }).then((res) => {
            const tests = res.data?.data || [];
            const options = [
                { value: NA_VALUE, label: "N/A" },
                ...tests
                    .filter((ct) => ct.v2_test)
                    .map((ct) => ({
                        value: String(ct.v2_test.id),
                        label: ct.v2_test.title || "(untitled)",
                    })),
            ];
            setTestOptions(options);

            const validValues = new Set(options.map((o) => o.value));
            const staleSlots = [];
            if (testRef1 !== NA_VALUE && testRef1 !== "" && !validValues.has(testRef1)) {
                setTestRef1(NA_VALUE);
                staleSlots.push(1);
            }
            if (testRef2 !== NA_VALUE && testRef2 !== "" && !validValues.has(testRef2)) {
                setTestRef2(NA_VALUE);
                staleSlots.push(2);
            }
            if (testRef3 !== NA_VALUE && testRef3 !== "" && !validValues.has(testRef3)) {
                setTestRef3(NA_VALUE);
                staleSlots.push(3);
            }
            if (staleSlots.length > 0) {
                setStaleTestRefWarning(
                    `Slot${staleSlots.length > 1 ? "s" : ""} ${staleSlots.join(", ")}: the previously selected test is no longer assigned to this course. Auto-set to N/A — please re-select if needed.`
                );
            }
        }).catch((err) => {
            console.log("Failed to load course tests:", err);
            setTestOptions([{ value: NA_VALUE, label: "N/A" }]);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId]);

    // AI Evaluation state
    const [aiEvalState, setAiEvalState] = useState(() => {
        // Initialize from special_notes
        const state = {};
        if (special_notes) {
            special_notes.forEach(note => {
                state[note.id] = {
                    ai_eval: note.ai_eval || '',
                    ai_eval_en: note.ai_eval_en || '',
                    ai_eval_published: note.ai_eval_published || false,
                };
            });
        }
        return state;
    });
    const [aiEvalLoading, setAiEvalLoading] = useState({});
    const [editingAiEval, setEditingAiEval] = useState({});
    // Track pending regeneration for comparison view
    const [pendingRegeneration, setPendingRegeneration] = useState({});

    // Track if any AI eval changes were made
    const [aiEvalDirty, setAiEvalDirty] = useState(false);

    // AI Eval handlers - update local state, reload on exit
    const handleRegenerateAiEval = async (noteId) => {
        setAiEvalLoading(prev => ({ ...prev, [noteId]: true }));
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/special-notes/${noteId}/ai-eval/regenerate`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            // Store in pending state for comparison
            setPendingRegeneration(prev => ({
                ...prev,
                [noteId]: {
                    ai_eval: response.data.ai_eval,
                    ai_eval_en: response.data.ai_eval_en,
                }
            }));
            setSuccessAlertMessage("New AI evaluation generated. Review and Accept or Revert.");
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to regenerate AI evaluation");
            setErrorAlertOpen(true);
        } finally {
            setAiEvalLoading(prev => ({ ...prev, [noteId]: false }));
        }
    };

    const handleAcceptRegenerated = (noteId) => {
        const pending = pendingRegeneration[noteId];
        if (pending) {
            setAiEvalState(prev => ({
                ...prev,
                [noteId]: {
                    ...prev[noteId],
                    ai_eval: pending.ai_eval,
                    ai_eval_en: pending.ai_eval_en,
                }
            }));
            setPendingRegeneration(prev => {
                const newState = { ...prev };
                delete newState[noteId];
                return newState;
            });
            setAiEvalDirty(true);
            setSuccessAlertMessage("New AI evaluation accepted!");
            setSuccessAlertOpen(true);
        }
    };

    const handleRevertRegenerated = async (noteId) => {
        // Revert means we need to restore the original from the server
        const current = aiEvalState[noteId];
        setAiEvalLoading(prev => ({ ...prev, [noteId]: true }));
        try {
            // Save the original back to the server (since regenerate already saved the new one)
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/special-notes/${noteId}/ai-eval`,
                { ai_eval: current.ai_eval, ai_eval_en: current.ai_eval_en },
                { headers: { Authorization: getAuthHeader() } }
            );
            setPendingRegeneration(prev => {
                const newState = { ...prev };
                delete newState[noteId];
                return newState;
            });
            setAiEvalDirty(true); // Mark dirty so page reloads on Done
            setSuccessAlertMessage("Reverted to original AI evaluation.");
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to revert");
            setErrorAlertOpen(true);
        } finally {
            setAiEvalLoading(prev => ({ ...prev, [noteId]: false }));
        }
    };

    const handleSaveAiEval = async (noteId) => {
        setAiEvalLoading(prev => ({ ...prev, [noteId]: true }));
        try {
            const editData = editingAiEval[noteId];
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/special-notes/${noteId}/ai-eval`,
                { ai_eval: editData.kr, ai_eval_en: editData.en },
                { headers: { Authorization: getAuthHeader() } }
            );
            setAiEvalState(prev => ({
                ...prev,
                [noteId]: {
                    ...prev[noteId],
                    ai_eval: editData.kr,
                    ai_eval_en: editData.en,
                }
            }));
            setEditingAiEval(prev => {
                const newState = { ...prev };
                delete newState[noteId];
                return newState;
            });
            setAiEvalDirty(true);
            setSuccessAlertMessage("AI evaluation saved successfully!");
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to save AI evaluation");
            setErrorAlertOpen(true);
        } finally {
            setAiEvalLoading(prev => ({ ...prev, [noteId]: false }));
        }
    };

    const handleDeleteAiEval = async (noteId) => {
        setAiEvalLoading(prev => ({ ...prev, [noteId]: true }));
        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/special-notes/${noteId}/ai-eval`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setAiEvalState(prev => ({
                ...prev,
                [noteId]: {
                    ai_eval: '',
                    ai_eval_en: '',
                    ai_eval_published: false,
                }
            }));
            setAiEvalDirty(true);
            setSuccessAlertMessage("AI evaluation deleted successfully!");
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to delete AI evaluation");
            setErrorAlertOpen(true);
        } finally {
            setAiEvalLoading(prev => ({ ...prev, [noteId]: false }));
        }
    };

    const handlePublishAiEval = async (noteId) => {
        setAiEvalLoading(prev => ({ ...prev, [noteId]: true }));
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/special-notes/${noteId}/ai-eval/publish`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setAiEvalState(prev => ({
                ...prev,
                [noteId]: {
                    ...prev[noteId],
                    ai_eval_published: true,
                }
            }));
            setAiEvalDirty(true);
            setSuccessAlertMessage("AI evaluation published successfully!");
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to publish AI evaluation");
            setErrorAlertOpen(true);
        } finally {
            setAiEvalLoading(prev => ({ ...prev, [noteId]: false }));
        }
    };

    const handleUnpublishAiEval = async (noteId) => {
        setAiEvalLoading(prev => ({ ...prev, [noteId]: true }));
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/special-notes/${noteId}/ai-eval/unpublish`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setAiEvalState(prev => ({
                ...prev,
                [noteId]: {
                    ...prev[noteId],
                    ai_eval_published: false,
                }
            }));
            setAiEvalDirty(true);
            setSuccessAlertMessage("AI evaluation unpublished successfully!");
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to unpublish AI evaluation");
            setErrorAlertOpen(true);
        } finally {
            setAiEvalLoading(prev => ({ ...prev, [noteId]: false }));
        }
    };

    const handleAiEvalDone = () => {
        if (aiEvalDirty) {
            window.location.reload();
        } else {
            handleClose();
        }
    };

    const startEditAiEval = (noteId) => {
        const current = aiEvalState[noteId] || {};
        setEditingAiEval(prev => ({
            ...prev,
            [noteId]: { kr: current.ai_eval || '', en: current.ai_eval_en || '' }
        }));
    };

    const cancelEditAiEval = (noteId) => {
        setEditingAiEval(prev => {
            const newState = { ...prev };
            delete newState[noteId];
            return newState;
        });
    };
        

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "50%",
        height: "80%",
        overflow: 'scroll',
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        ...!matches && {
            width: "60%",
        },
        ...!matches2 && {
            width: "80%",
        },
    };

    const checkoutSchema = yup.object().shape({
        chapter_name: yup.string().required("required"),
        from: yup.number().required("required"),
        to: yup.number().required("required"),
        chapter_name_2: yup.string().optional(),
        from_2: yup.number().optional(),
        to_2: yup.number().optional(),
        chapter_name_3: yup.string().optional(),
        from_3: yup.number().optional(),
        to_3: yup.number().optional(),
    });
        
    const initialValues = {
        chapter_name: prevChapter[0] || "",
        from: prevFrom[0] || "",
        to: prevTo[0] || "",
        chapter_name_2: prevChapter2[0] || "",
        from_2: prevFrom2[0] || "",
        to_2: prevTo2[0] || "",
        chapter_name_3: prevChapter3[0] || "",
        from_3: prevFrom3[0] || "",
        to_3: prevTo3[0] || "",
    };

    const [firstRender, setFirstRender] = React.useState(true);

    // get students based on event
    const [studentsBasedOnEvent, setStudentsBasedOnEvent] = React.useState([]);

    if (firstRender) {
        const event_start = dayjs(eventModule.start);
        const students = [];
        for (let i = 0; i < courseStudents.length; i++) {
            const existing_note = special_notes.find(
                (note) => note.student_id === courseStudents[i].student_id
            );

            // a student who already has a log on this event always stays listed, so logs
            // written before this check existed remain editable. a student without one is
            // only offered when their enrollment window covers the event
            if (!existing_note) {
                const started = !courseStudents[i].start_date
                    || dayjs(courseStudents[i].start_date).subtract(1, 'day').isBefore(event_start);
                const not_ended = dayjs(courseStudents[i].end_date).add(1, 'day').isAfter(event_start);

                if (!started || !not_ended) {
                    continue;
                }
            }

            students.push({
                ...courseStudents[i],
                special_note_id: existing_note ? existing_note.id : null,
                eval_1: existing_note ? existing_note.eval_1 : "",
                eval_2: existing_note ? existing_note.eval_2 : "",
            });
        }

        setStudentsBasedOnEvent(students);
        setFirstRender(false);
    }

    // requirment check for course students evaluation
    const [courseStudentsEvaluation, setCourseStudentsEvaluation] = React.useState([]);

    React.useEffect(() => {
        setCourseStudentsEvaluation(
            studentsBasedOnEvent.length > 0 ? studentsBasedOnEvent.map((element) => {
                const eval_1 = element.eval_1
                const eval_2 = element.eval_2
                const special_note_id = element.special_note_id
    
                return {
                    id: element.student_id,
                    first_name: element.student.first_name,
                    last_name: element.student.last_name,
                    eval_1: eval_1 === undefined || eval_1 === null || eval_1 === "" ? false : true,
                    eval_1_data: eval_1,
                    prev_eval_1: eval_1,
                    eval_2: eval_2 === undefined || eval_2 === null || eval_2 === "" ? false : true,
                    eval_2_data: eval_2,
                    prev_eval_2: eval_2,
                    special_note_id: special_note_id,
                }
            }) : []    
        )
    }, [studentsBasedOnEvent]);

    const handleEvaluationChange = (event) => {
        setCourseStudentsEvaluation(courseStudentsEvaluation.map((element) => {
            if (element.id === parseInt(event.target.name.split('-')[0])) {
                if (event.target.name.split('-')[1] === "eval_1")
                    return {
                        ...element,
                        eval_1: event.target.value !== "" ? true : false,
                        eval_1_data: event.target.value
                    }
                else if (event.target.name.split('-')[1] === "eval_2")
                    return {
                        ...element,
                        eval_2: event.target.value !== "" ? true : false,
                        eval_2_data: event.target.value
                    }
            }
            return element;
        }));
    };

    const progressErrorCheck = (values) => {
        if (values.chapter_name === "" || values.chapter_name === null) {
            setErrorAlertMessage("Progress cannot be empty.");
            setErrorAlertOpen(true);
            return false;
        }

        if (values.chapter_name.length > 250) {
            setErrorAlertMessage("Progress is too long.");
            setErrorAlertOpen(true);
            return false;
        }

        if (isNaN(values.from) || isNaN(values.to)) {
            setErrorAlertMessage("Progress 'From' and 'To' must be a number.");
            setErrorAlertOpen(true);
            return false;
        }

        if (parseInt(values.from) > parseInt(values.to)) {
            setErrorAlertMessage("Progress 'From' cannot be greater than progress 'To'.");
            setErrorAlertOpen(true);
            return false;
        }

        if (values.from < 0 || values.to < 0) {
            setErrorAlertMessage("Progress 'From' and 'To' cannot be negative.");
            setErrorAlertOpen(true);
            return false;
        }

        if (isProgressAdded <= 1)
            return true;

        if (values.chapter_name_2 === "" || values.chapter_name_2 === null) {
            setErrorAlertMessage("Progress cannot be empty.");
            setErrorAlertOpen(true);
            return false;
        }

        if (values.chapter_name_2.length > 250) {
            setErrorAlertMessage("Progress is too long.");
            setErrorAlertOpen(true);
            return false;
        }

        if (isNaN(values.from_2) || isNaN(values.to_2)) {
            setErrorAlertMessage("Progress 'From' and 'To' must be a number.");
            setErrorAlertOpen(true);
            return false;
        }

        if (parseInt(values.from_2) > parseInt(values.to_2)) {
            setErrorAlertMessage("Progress 'From' cannot be greater than progress 'To'.");
            setErrorAlertOpen(true);
            return false;
        }

        if (values.from_2 < 0 || values.to_2 < 0) {
            setErrorAlertMessage("Progress 'From' and 'To' cannot be negative.");
            setErrorAlertOpen(true);
            return false;
        }

        if (isProgressAdded <= 2)
            return true;

        if (values.chapter_name_3 === "" || values.chapter_name_3 === null) {
            setErrorAlertMessage("Progress cannot be empty.");
            setErrorAlertOpen(true);
            return false;
        }

        if (values.chapter_name_3.length > 250) {
            setErrorAlertMessage("Progress is too long.");
            setErrorAlertOpen(true);
            return false;
        }

        if (isNaN(values.from_3) || isNaN(values.to_3)) {
            setErrorAlertMessage("Progress 'From' and 'To' must be a number.");
            setErrorAlertOpen(true);
            return false;
        }

        if (parseInt(values.from_3) > parseInt(values.to_3)) {
            setErrorAlertMessage("Progress 'From' cannot be greater than progress 'To'.");
            setErrorAlertOpen(true);
            return false;
        }

        if (values.from_3 < 0 || values.to_3 < 0) {
            setErrorAlertMessage("Progress 'From' and 'To' cannot be negative.");
            setErrorAlertOpen(true);
            return false;
        }

        return true;
    };

    const progressDirtyCheck = (values) => {
        if (firstProgressCount[0] !== isProgressAdded)
            return true;
        if (values.chapter_name !== prevChapter[0] || values.from !== prevFrom[0] || values.to !== prevTo[0])
            return true;
        if (isProgressAdded >= 2) {
            if (values.chapter_name_2 !== prevChapter2[0] || values.from_2 !== prevFrom2[0] || values.to_2 !== prevTo2[0])
                return true;
        }
        if (isProgressAdded >= 3) {
            if (values.chapter_name_3 !== prevChapter3[0] || values.from_3 !== prevFrom3[0] || values.to_3 !== prevTo3[0])
                return true;
        }
        if (testRef1 !== prevTestRef1[0]) return true;
        if (isProgressAdded >= 2 && testRef2 !== prevTestRef2[0]) return true;
        if (isProgressAdded >= 3 && testRef3 !== prevTestRef3[0]) return true;
        return false;
    };

    const handleFormSubmit = async (values) => {
        setBackdropOpen(true);

        // check if all students have been evaluated
        let allEvaluated = true;
        courseStudentsEvaluation.forEach((element) => {
            if (!element.eval_1 || !element.eval_2) {
                allEvaluated = false;
            }
        });

        if (!allEvaluated) {
            setErrorAlertMessage("Please evaluate all students before submitting.");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        let doesRequestHaveError = false;
        let errorMsg = "";

        const progress = values.chapter_name;
        const progress_from = values.from;
        const progress_to = values.to;

        if (!progressErrorCheck(values)) {
            setBackdropOpen(false);
            return;
        }

        // test_ref selection check — required for each visible progress slot
        if (testRef1 === "") {
            setErrorAlertMessage("Please select a test (or N/A) for chapter 1.");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }
        if (isProgressAdded >= 2 && testRef2 === "") {
            setErrorAlertMessage("Please select a test (or N/A) for chapter 2.");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }
        if (isProgressAdded >= 3 && testRef3 === "") {
            setErrorAlertMessage("Please select a test (or N/A) for chapter 3.");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        const toApiTestRef = (val) => {
            if (val === "" || val === NA_VALUE) return null;
            return parseInt(val, 10);
        };

        // update event module
        if (progressDirtyCheck(values)) {
            await Axios.put(`${process.env.REACT_APP_URL}/api/v1/events/${eventModule.id}/progress`, {
                progress,
                progress_from,
                progress_to,
                progress_2: isProgressAdded >= 2 ? values.chapter_name_2 : null,
                progress_from_2: isProgressAdded >= 2 ? values.from_2 : null,
                progress_to_2: isProgressAdded >= 2 ? values.to_2 : null,
                progress_3: isProgressAdded >= 3 ? values.chapter_name_3 : null,
                progress_from_3: isProgressAdded >= 3 ? values.from_3 : null,
                progress_to_3: isProgressAdded >= 3 ? values.to_3 : null,
                progress_count: isProgressAdded,
                test_ref_1: toApiTestRef(testRef1),
                test_ref_2: isProgressAdded >= 2 ? toApiTestRef(testRef2) : null,
                test_ref_3: isProgressAdded >= 3 ? toApiTestRef(testRef3) : null,
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                console.log(response);
            }).catch((error) => {
                console.log(error);
                doesRequestHaveError = true;
                errorMsg = error.response?.data?.message || "Something went wrong";
            });
        }

        if (!doesRequestHaveError) {
            // update special notes
            const special_notes_data = [];
            const created_special_notes = [];
            for (let i = 0; i < courseStudentsEvaluation.length; i++) {
                const student_evaluation = courseStudentsEvaluation[i];
                const is_dirty = student_evaluation.eval_1_data !== student_evaluation.prev_eval_1
                    || student_evaluation.eval_2_data !== student_evaluation.prev_eval_2;

                if (!is_dirty) {
                    continue;
                }

                if (student_evaluation.special_note_id) {
                    special_notes_data.push({
                        id: student_evaluation.special_note_id,
                        student_id: student_evaluation.id,
                        eval_1: student_evaluation.eval_1_data,
                        eval_2: student_evaluation.eval_2_data,
                    });
                } else {
                    // student had no log on this event yet — create one instead of
                    // updating, so the teacher does not have to go back to the create form
                    created_special_notes.push({
                        student_id: student_evaluation.id,
                        eval_1: student_evaluation.eval_1_data,
                        eval_2: student_evaluation.eval_2_data,
                    });
                }
            }

            if (special_notes_data.length > 0) {
                await Axios.post(`${process.env.REACT_APP_URL}/api/v1/events/${eventModule.id}/special-notes/update`, {
                    all_special_notes: special_notes_data,
                }, {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }).catch((error) => {
                    doesRequestHaveError = true;
                    errorMsg = error.response?.data?.message || "Something went wrong.";
                });
            }

            if (!doesRequestHaveError && created_special_notes.length > 0) {
                await Axios.post(`${process.env.REACT_APP_URL}/api/v1/events/${eventModule.id}/special-notes`, {
                    all_special_notes: created_special_notes,
                }, {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }).catch((error) => {
                    doesRequestHaveError = true;
                    errorMsg = error.response?.data?.message || "Something went wrong.";
                });
            }
        }

        if (doesRequestHaveError) {
            setErrorAlertMessage(errorMsg);
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        setBackdropOpen(false);
        setSuccessAlertOpen(true);
        setSuccessAlertMessage("Successfully updated the class log.");
        window.location.reload();
    };

    return (
        <div>
            <Button onClick={handleOpen} sx={{ p: 1, width: "100%" }}>Update</Button>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
                sx={{
                    height: "100%",
                    overflow: "scroll",
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Formik
                            onSubmit={handleFormSubmit}
                            initialValues={initialValues}
                            validationSchema={checkoutSchema}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                            }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    padding={4}
                                    boxShadow={3}
                                    borderRadius={3}
                                    m={1}
                                    bgcolor={colors.primary[600]}
                                >
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        mb={3}
                                    >
                                        Update {eventModule.title}'s log
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        mb={1}
                                    >
                                        Progress
                                    </Typography>
                                    {staleTestRefWarning && (
                                        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setStaleTestRefWarning(null)}>
                                            {staleTestRefWarning}
                                        </Alert>
                                    )}
                                    <Box
                                        display="grid"
                                        gap="30px"
                                        gridTemplateColumns="repeat(6, minmax(0, 1fr))"
                                        mb={1}
                                        sx={{
                                            "& > div": { gridColumn: isNonMobile ? undefined : "span 6" },
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Name of Chapter"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.chapter_name}
                                            name="chapter_name"
                                            error={!!touched.chapter_name && !!errors.chapter_name}
                                            helperText={touched.chapter_name && errors.chapter_name}
                                            sx={{ gridColumn: "span 2" }}
                                        />
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="From (Page)"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.from}
                                            name="from"
                                            error={!!touched.from && !!errors.from}
                                            helperText={touched.from && errors.from}
                                            sx={{ gridColumn: "span 1" }}
                                        />
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="To"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.to}
                                            name="to"
                                            error={!!touched.to && !!errors.to}
                                            helperText={touched.to && errors.to}
                                            sx={{ gridColumn: "span 1" }}
                                        />
                                        <Box sx={{ gridColumn: "span 2" }}>
                                            <Dropdown
                                                maxWidth="100%"
                                                maxHeight={3}
                                                reactUseState={testRef1}
                                                setReactUseState={setTestRef1}
                                                inputLabel="Select Test"
                                                data={testOptions}
                                            />
                                        </Box>
                                    </Box>
                                    {isProgressAdded >= 2 && (
                                        <Box
                                            display="grid"
                                            gap="30px"
                                            gridTemplateColumns="repeat(6, minmax(0, 1fr))"
                                            mb={1}
                                            position="relative"
                                            sx={{
                                                "& > div": { gridColumn: isNonMobile ? undefined : "span 6" },
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="text"
                                                label="Name of Chapter"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.chapter_name_2}
                                                name="chapter_name_2"
                                                error={!!touched.chapter_name_2 && !!errors.chapter_name_2}
                                                helperText={touched.chapter_name_2 && errors.chapter_name_2}
                                                sx={{ gridColumn: "span 2" }}
                                            />
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="text"
                                                label="From (Page)"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.from_2}
                                                name="from_2"
                                                error={!!touched.from_2 && !!errors.from_2}
                                                helperText={touched.from_2 && errors.from_2}
                                                sx={{ gridColumn: "span 1" }}
                                            />
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="text"
                                                label="To"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.to_2}
                                                name="to_2"
                                                error={!!touched.to_2 && !!errors.to_2}
                                                helperText={touched.to_2 && errors.to_2}
                                                sx={{ gridColumn: "span 1" }}
                                            />
                                            <Box sx={{ gridColumn: "span 2" }}>
                                                <Dropdown
                                                    maxWidth="100%"
                                                    maxHeight={3}
                                                    reactUseState={testRef2}
                                                    setReactUseState={setTestRef2}
                                                    inputLabel="Select Test"
                                                    data={testOptions}
                                                />
                                            </Box>
                                            <CloseIcon
                                                color='error'
                                                onClick={() => setIsProgressAdded(isProgressAdded - 1)}
                                                sx={{
                                                    cursor: "pointer",
                                                    position: "absolute",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    right: "-20px",
                                                    zIndex: "100",
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {isProgressAdded >= 3 && (
                                        <Box
                                            display="grid"
                                            gap="30px"
                                            gridTemplateColumns="repeat(6, minmax(0, 1fr))"
                                            position="relative"
                                            sx={{
                                                "& > div": { gridColumn: isNonMobile ? undefined : "span 6" },
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="text"
                                                label="Name of Chapter"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.chapter_name_3}
                                                name="chapter_name_3"
                                                error={!!touched.chapter_name_3 && !!errors.chapter_name_3}
                                                helperText={touched.chapter_name_3 && errors.chapter_name_3}
                                                sx={{ gridColumn: "span 2" }}
                                            />
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="text"
                                                label="From (Page)"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.from_3}
                                                name="from_3"
                                                error={!!touched.from_3 && !!errors.from_3}
                                                helperText={touched.from_3 && errors.from_3}
                                                sx={{ gridColumn: "span 1" }}
                                            />
                                            <TextField
                                                fullWidth
                                                variant="filled"
                                                type="text"
                                                label="To"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.to_3}
                                                name="to_3"
                                                error={!!touched.to_3 && !!errors.to_3}
                                                helperText={touched.to_3 && errors.to_3}
                                                sx={{ gridColumn: "span 1" }}
                                            />
                                            <Box sx={{ gridColumn: "span 2" }}>
                                                <Dropdown
                                                    maxWidth="100%"
                                                    maxHeight={3}
                                                    reactUseState={testRef3}
                                                    setReactUseState={setTestRef3}
                                                    inputLabel="Select Test"
                                                    data={testOptions}
                                                />
                                            </Box>
                                            <CloseIcon
                                                color='error'
                                                onClick={() => setIsProgressAdded(isProgressAdded - 1)}
                                                sx={{
                                                    cursor: "pointer",
                                                    position: "absolute",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    right: "-20px",
                                                    zIndex: "100",
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {isProgressAdded < 3 && (
                                        <Button
                                            variant="contained"
                                            style={{
                                                backgroundColor: colors.blueAccent[600],
                                                color: "white",
                                                width: "12rem",
                                                marginTop: "20px",
                                            }}
                                            onClick={() => {
                                                setIsProgressAdded(isProgressAdded + 1);
                                            }}
                                        >
                                            Add Chapter
                                        </Button>
                                    )}

                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        mb={1}
                                        mt={2}
                                    >
                                        Student Evaluations
                                    </Typography>
                                    {courseStudentsEvaluation && courseStudentsEvaluation.map((student, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                width: "100%",
                                                marginBottom: "1rem",
                                            }}
                                        >
                                            <Accordion
                                                key={index}
                                                style={{
                                                    backgroundColor: colors.primary[750],
                                                }}
                                                component="div"
                                                defaultExpanded={index === 0 ? true : false}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    aria-controls="panel1a-content"
                                                    id="panel1a-header"
                                                >
                                                    <Typography variant='h6' mr={1}>
                                                        {student.first_name + " " + student.last_name}
                                                    </Typography>
                                                    {student.eval_1 && student.eval_2 ?
                                                        <CheckSharpIcon color='success' /> : <ClearSharpIcon color='error' />
                                                    }
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                    >
                                                        <TextField
                                                            id="filled-basic"
                                                            label="Evaluation: Student's good at this..."
                                                            variant="filled"
                                                            name={student.id + "-eval_1"}
                                                            fullWidth={true}
                                                            multiline={true}
                                                            rows={5}
                                                            sx={{
                                                                minWidth: "40%",
                                                                borderRadius: "5px",
                                                                '& .MuiFilledInput-root': {
                                                                    ":focus": {
                                                                        backgroundColor: 'white',
                                                                    },
                                                                },
                                                            }}
                                                            defaultValue={student.eval_1_data}
                                                            onChange={handleEvaluationChange}
                                                        />
                                                        <TextField
                                                            id="filled-basic"
                                                            label="Evaluation: Student needs to improve on this..."
                                                            variant="filled"
                                                            name={student.id + "-eval_2"}
                                                            fullWidth={true}
                                                            multiline={true}
                                                            rows={5}
                                                            sx={{
                                                                minWidth: "40%",
                                                                borderRadius: "5px",
                                                                '& .MuiFilledInput-root': {
                                                                    ":focus": {
                                                                        backgroundColor: 'white',
                                                                    },
                                                                },
                                                            }}
                                                            defaultValue={student.eval_2_data}
                                                            onChange={handleEvaluationChange}
                                                        />
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        </div>
                                    ))}

                                    <Box display="flex" justifyContent="center" mt="20px" mb={4}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            style={{
                                                backgroundColor: colors.primary[300],
                                                color: "white",
                                                width: "10rem",
                                            }}
                                        >
                                            Update
                                        </Button>
                                    </Box>
                                </Box>
                            </form>
                            )}
                        </Formik>

                        {/* AI Evaluation Section - Admin only */}
                        {userRole === "admin" && (
                        <Box
                            padding={4}
                            boxShadow={3}
                            borderRadius={3}
                            m={1}
                            mt={3}
                            bgcolor={colors.primary[600]}
                            sx={{ border: '2px solid', borderColor: 'info.main' }}
                        >
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                mb={1}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <AutoAwesomeIcon color="info" /> AI Evaluations (Parent Portal)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                Generate, edit, and publish AI evaluations. Changes are saved immediately. Press Done to exit and refresh.
                            </Typography>

                            {studentsBasedOnEvent && studentsBasedOnEvent.map((student, index) => {
                                const noteId = student.special_note_id;

                                // a student being added to this log for the first time has no
                                // note yet, so there is nothing to generate or publish from
                                if (!noteId) {
                                    return null;
                                }

                                const aiEval = aiEvalState[noteId] || {};
                                const isLoading = aiEvalLoading[noteId];
                                const isEditing = editingAiEval.hasOwnProperty(noteId);
                                const hasAiEval = aiEval.ai_eval || aiEval.ai_eval_en;

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: colors.primary[750],
                                            border: '1px solid',
                                            borderColor: aiEval.ai_eval_published ? 'success.main' : 'divider',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {student.student.first_name} {student.student.last_name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {hasAiEval && (
                                                    <Chip
                                                        size="small"
                                                        label={aiEval.ai_eval_published ? "Published" : "Unpublished"}
                                                        color={aiEval.ai_eval_published ? "success" : "default"}
                                                        icon={aiEval.ai_eval_published ? <PublishIcon /> : <UnpublishedIcon />}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        {isLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                                <CircularProgress size={28} />
                                            </Box>
                                        ) : isEditing ? (
                                            <Box>
                                                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                                                    Korean (KR):
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={editingAiEval[noteId]?.kr || ''}
                                                    onChange={(e) => setEditingAiEval(prev => ({
                                                        ...prev,
                                                        [noteId]: { ...prev[noteId], kr: e.target.value }
                                                    }))}
                                                    sx={{ mb: 1 }}
                                                />
                                                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                                                    English (EN):
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    value={editingAiEval[noteId]?.en || ''}
                                                    onChange={(e) => setEditingAiEval(prev => ({
                                                        ...prev,
                                                        [noteId]: { ...prev[noteId], en: e.target.value }
                                                    }))}
                                                    sx={{ mb: 1 }}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => cancelEditAiEval(noteId)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleSaveAiEval(noteId)}
                                                    >
                                                        Save
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ) : hasAiEval ? (
                                            <Box>
                                                {/* Check if there's a pending regeneration to show comparison view */}
                                                {pendingRegeneration[noteId] ? (
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                            <CompareArrowsIcon color="info" />
                                                            <Typography variant="subtitle2" color="info.main" fontWeight="bold">
                                                                Review Changes - Compare old vs new evaluation
                                                            </Typography>
                                                        </Box>

                                                        {/* Side-by-side comparison */}
                                                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                            {/* Current/Old evaluation */}
                                                            <Box sx={{ flex: 1, p: 2, borderRadius: 1, backgroundColor: 'rgba(255, 152, 0, 0.1)', border: '1px solid', borderColor: 'warning.main' }}>
                                                                <Typography variant="caption" color="warning.main" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                                                    CURRENT (will be replaced)
                                                                </Typography>
                                                                <Typography variant="caption" fontWeight="bold">Korean (KR):</Typography>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1, fontSize: '0.8rem' }}>
                                                                    {aiEval.ai_eval || <em style={{ color: '#999' }}>No Korean version</em>}
                                                                </Typography>
                                                                <Typography variant="caption" fontWeight="bold">English (EN):</Typography>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                                                                    {aiEval.ai_eval_en || <em style={{ color: '#999' }}>No English version</em>}
                                                                </Typography>
                                                            </Box>

                                                            {/* New evaluation */}
                                                            <Box sx={{ flex: 1, p: 2, borderRadius: 1, backgroundColor: 'rgba(76, 175, 80, 0.1)', border: '1px solid', borderColor: 'success.main' }}>
                                                                <Typography variant="caption" color="success.main" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                                                    NEW (just generated)
                                                                </Typography>
                                                                <Typography variant="caption" fontWeight="bold">Korean (KR):</Typography>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1, fontSize: '0.8rem' }}>
                                                                    {pendingRegeneration[noteId].ai_eval || <em style={{ color: '#999' }}>No Korean version</em>}
                                                                </Typography>
                                                                <Typography variant="caption" fontWeight="bold">English (EN):</Typography>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                                                                    {pendingRegeneration[noteId].ai_eval_en || <em style={{ color: '#999' }}>No English version</em>}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Action buttons for comparison mode */}
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="warning"
                                                                startIcon={<UndoIcon />}
                                                                onClick={() => handleRevertRegenerated(noteId)}
                                                            >
                                                                Revert (Keep Current)
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<CheckIcon />}
                                                                onClick={() => handleAcceptRegenerated(noteId)}
                                                            >
                                                                Accept New
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        <Box sx={{ mb: 1 }}>
                                                            <Typography variant="caption" color="primary" fontWeight="bold">Korean (KR):</Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                                                {aiEval.ai_eval || <em style={{ color: '#999' }}>No Korean version</em>}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="caption" color="primary" fontWeight="bold">English (EN):</Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                                                {aiEval.ai_eval_en || <em style={{ color: '#999' }}>No English version</em>}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<RefreshIcon />}
                                                                onClick={() => handleRegenerateAiEval(noteId)}
                                                            >
                                                                Regenerate
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => startEditAiEval(noteId)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<DeleteIcon />}
                                                                onClick={() => handleDeleteAiEval(noteId)}
                                                            >
                                                                Delete
                                                            </Button>
                                                            {aiEval.ai_eval_published ? (
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="warning"
                                                                    startIcon={<UnpublishedIcon />}
                                                                    onClick={() => handleUnpublishAiEval(noteId)}
                                                                >
                                                                    Unpublish
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="success"
                                                                    startIcon={<PublishIcon />}
                                                                    onClick={() => handlePublishAiEval(noteId)}
                                                                >
                                                                    Publish
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    No AI evaluation generated yet
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<AutoAwesomeIcon />}
                                                    onClick={() => handleRegenerateAiEval(noteId)}
                                                >
                                                    Generate AI Evaluation
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}

                            <Box display="flex" justifyContent="center" mt={3}>
                                <Button
                                    variant="contained"
                                    onClick={handleAiEvalDone}
                                    style={{
                                        backgroundColor: colors.blueAccent[600],
                                        color: "white",
                                        width: "10rem",
                                    }}
                                >
                                    Done
                                </Button>
                            </Box>
                        </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default UpdateModal